/**
 * Composant carte de métrique pour le dashboard de monitoring
 * Affiche une métrique clé avec changement et statut
 * @author Cascade (Tech Lead)
 * @date 2025-07-09
 */

import React from 'react';
import type { MetricsCardProps } from '../../../types/monitoring.types';

const MetricsCard: React.FC<MetricsCardProps> = ({
  title,
  value,
  change,
  changeType = 'neutral',
  icon,
  isLoading = false,
  className = ''
}) => {
  const formatValue = (val: string | number): string => {
    if (typeof val === 'number') {
      // Format des nombres selon le type
      if (title.toLowerCase().includes('cost') || title.toLowerCase().includes('coût')) {
        return `$${val.toFixed(2)}`;
      }
      if (title.toLowerCase().includes('time') || title.toLowerCase().includes('temps')) {
        return `${val}ms`;
      }
      if (title.toLowerCase().includes('rate') || title.toLowerCase().includes('taux')) {
        return `${val}%`;
      }
      return val.toLocaleString();
    }
    return val.toString();
  };

  const getChangeColor = (type: typeof changeType): string => {
    switch (type) {
      case 'increase':
        return 'text-green-600';
      case 'decrease':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getChangeIcon = (type: typeof changeType): string => {
    switch (type) {
      case 'increase':
        return '↗';
      case 'decrease':
        return '↘';
      default:
        return '→';
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 border border-gray-200 ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            <div className="h-6 w-6 bg-gray-300 rounded"></div>
          </div>
          <div className="h-8 bg-gray-300 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-300 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-200 ${className}`}>
      {/* Header avec titre et icône */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">
          {title}
        </h3>
        {icon && (
          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
            {icon}
          </div>
        )}
      </div>

      {/* Valeur principale */}
      <div className="flex items-baseline justify-between">
        <div className="text-3xl font-bold text-gray-900">
          {formatValue(value)}
        </div>
        
        {/* Changement (si fourni) */}
        {change !== undefined && (
          <div className={`flex items-center text-sm font-medium ${getChangeColor(changeType)}`}>
            <span className="mr-1">{getChangeIcon(changeType)}</span>
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>

      {/* Indicateur de changement textuel */}
      {change !== undefined && (
        <div className={`mt-2 text-xs ${getChangeColor(changeType)}`}>
          {changeType === 'increase' && 'En hausse'}
          {changeType === 'decrease' && 'En baisse'}
          {changeType === 'neutral' && 'Stable'}
          {' par rapport à la période précédente'}
        </div>
      )}
    </div>
  );
};

export default MetricsCard;
